import React from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'ghost'
  | 'outline-primary'
  | 'outline-danger'
  | 'outline-success'
  | 'soft-danger'
  | 'soft-success';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
  iconOnly?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Solid — for primary CTAs and high-emphasis actions
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 shadow-sm',
  secondary:
    'bg-surface text-content-secondary border border-border hover:bg-surface-secondary focus:ring-primary-500 shadow-sm',
  danger:
    'bg-danger-600 text-white hover:bg-danger-700 focus:ring-danger-500 shadow-sm',
  success:
    'bg-success-600 text-white hover:bg-success-700 focus:ring-success-500 shadow-sm',
  ghost:
    'bg-transparent text-content-secondary hover:bg-surface-tertiary hover:text-content-primary focus:ring-primary-500',

  // Outline — for medium-emphasis actions (table rows, secondary CTAs)
  'outline-primary':
    'bg-transparent text-primary-600 border border-primary-300 hover:bg-primary-50 hover:border-primary-400 focus:ring-primary-500',
  'outline-danger':
    'bg-transparent text-danger-600 border border-danger-200 hover:bg-danger-50 hover:border-danger-300 focus:ring-danger-500',
  'outline-success':
    'bg-transparent text-success-600 border border-success-300 hover:bg-success-50 hover:border-success-400 focus:ring-success-500',

  // Soft — for low-emphasis actions in dense UIs (inline table actions)
  'soft-danger':
    'bg-danger-50 text-danger-700 hover:bg-danger-100 focus:ring-danger-500',
  'soft-success':
    'bg-success-50 text-success-700 hover:bg-success-100 focus:ring-success-500',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'px-2 py-1 text-xs gap-1 rounded-md',
  sm: 'px-2.5 py-1.5 text-xs gap-1.5 rounded-md',
  md: 'px-3.5 py-2 text-sm gap-2 rounded-lg',
  lg: 'px-5 py-2.5 text-sm gap-2 rounded-lg',
};

const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  xs: 'p-1 rounded-md',
  sm: 'p-1.5 rounded-md',
  md: 'p-2 rounded-lg',
  lg: 'p-2.5 rounded-lg',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading = false,
  iconOnly = false,
  disabled,
  children,
  className = '',
  ...rest
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center font-medium
        focus:outline-none focus:ring-2 focus:ring-offset-1
        transition-all duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
        ${VARIANT_CLASSES[variant]}
        ${iconOnly ? ICON_ONLY_SIZE[size] : SIZE_CLASSES[size]}
        ${className}
      `}
      {...rest}
    >
      {loading ? (
        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : icon ? (
        <span className="flex-shrink-0">{icon}</span>
      ) : null}
      {!iconOnly && children && <span>{children}</span>}
      {!iconOnly && iconRight && <span className="flex-shrink-0">{iconRight}</span>}
    </button>
  );
};

export default Button;
